import { NavBar } from '@design-system/NavBar';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetImproveScoreDetail } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { getImageUrl } from '@utils/nutrition/getImageUrl';
import { getFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { useEffect, useState } from 'react';
import { HealthL4ImproveMyScoreDetailSkeleton } from './HealthL4ImproveMyScoreDetailSkeleton';
import { Button } from '@design-system/Button';
import { FORMULA_PATH, SHOP_PATH } from '@/constants/paths';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';

type ItemType = 'Foods' | 'Ingredients' | '';

export const HealthL4ImproveMyScoreDetail = () => {
  const { id: scoreId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState<ItemType>('');

  const { data: improveScoreDetailData, isLoading, isError } = useGetImproveScoreDetail('L4',  { score_id: Number(scoreId) });
  const { data: subscriptionData } = useGetSubscriptionStatusQuery();

  const { score_name, items: improveScoreList = [] } = improveScoreDetailData || {};
  const [categoryValueList, setCategoryValueList] = useState(improveScoreList);
  
  const hasActiveSubscription = subscriptionData?.subscription_details?.has_active_subscription;

  useEffect(() => {
    setCategoryValueList(improveScoreList);
  }, [improveScoreList]);

  const getScoreImageUrl = (item_type: string, item_url: string) => {
    if (item_type === 'Foods') {
      return getImageUrl(item_url);
    } else {
      return getFormulaImageUrl(item_url);
    }
  };

  const onChangeCategory = (categoryType: ItemType) => {
    if (categoryType === '') {
      setCategoryValueList(improveScoreList);
      setCategory('');
    } else {
      const filteredList = improveScoreList?.filter(
        (item) => item.item_type === categoryType,
      );
      setCategory(categoryType);
      setCategoryValueList(filteredList);
    }
  };

  const onClickNavigate = (itemType: ItemType, item_id: number) => {
    if (!itemType) {
      return;
    }
    const redirectionRoute = itemType === 'Foods' ? 'foods' : 'ingredients';
    navigate(`/nutrition/${redirectionRoute}/${item_id}`);
  };

  const handleButtonClick = () => {
    if (hasActiveSubscription) {
      navigate(FORMULA_PATH);
    } else {
      navigate(SHOP_PATH);
    }
  };

  if (isLoading) {
    return <HealthL4ImproveMyScoreDetailSkeleton />;
  }

  if (isError) {
    return <ErrorLoadingPage />
  }

  if(!improveScoreDetailData) return;

  return (
    <section className='w-full page-container'>
      <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-8" />
      <div className="flex flex-col gap-4 mt-12">
        <h1 className="typography-h1 text-primary max-w-[37rem]">
          Improve My {score_name}
        </h1>
      </div>
      <div className='mt-6'>
        <ToggleGroup
          type="single"
          variant="outline"
          onValueChange={onChangeCategory}
          value={category}
        >
          <ToggleGroupItem
            className="px-4 py-2 bg-white typography-btn-medium"
            value="Foods"
          >
            Foods
          </ToggleGroupItem>
          <ToggleGroupItem
            className="px-4 py-2 bg-white typography-btn-medium"
            value="Ingredients"
          >
            Ingredients
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-2 my-12'>
        {categoryValueList?.length > 0 && (
          <>
            {categoryValueList?.map((list) => {
              const { item_id, item_tag, item_title, item_type, item_url } = list || {};
              return (
                <button
                  onClick={() => onClickNavigate(item_type, item_id)}
                  className='w-full h-20 px-6 py-1 pr-5 rounded-md bg-white flex justify-between items-center text-left'
                  key={item_id}
                >
                  <div className="flex items-center gap-7">
                    <div className="h-16 flex items-center justify-center">
                      <img
                        src={getScoreImageUrl(item_type, item_url)}
                        alt={item_title}
                        className="w-10 h-10"
                      />
                    </div>

                    <div className="flex flex-col line-clamp-1">
                      <h3 className="typography-body-emphasis text-primary">
                        {item_title}
                      </h3>
                      <p className="typography-caption1 text-primary/70">
                        {item_tag}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </>
        )}
      </div>
      <div className="fixed bottom-16 md:bottom-4 md:left-32 left-0 w-full flex justify-center">
        <Button
          className="min-w-[300px] px-24"
          onClick={handleButtonClick}
        >
          {hasActiveSubscription ? 'See My Formulas' : 'Shop My Formulas'}
        </Button>
      </div>
    </section>
  )
}